import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../api/axios';
import { io } from 'socket.io-client';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await api.get('/users/me');
      const profile = res.data;
      setUser(profile);
      localStorage.setItem('user', JSON.stringify(profile));
    } catch (err) {
      // Token invalid — force logout
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const restoreUser = async () => {
      // If we have a saved user, restore it immediately so the UI doesn't
      // flash the login page on refresh. We'll validate the token (if any)
      // in the background and clear the session if validation fails.
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (e) {
          console.warn('AuthContext: failed to parse saved user', e);
          localStorage.removeItem('user');
        }
      }

      // If token exists, validate it by calling the profile endpoint.
      if (token) {
        try {
          const res = await api.get('/users/me');
          const profile = res.data;
          // Use profile from server (in case it's more up-to-date)
          setUser(profile);
          localStorage.setItem('user', JSON.stringify(profile));
        } catch (err) {
          // Token invalid/expired — clear storage and force logout
          console.warn('AuthContext: token validation failed', err?.message || err);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }

      setLoading(false);
    };

    restoreUser();

    // Re-fetch fresh permissions whenever the tab regains focus so that
    // role changes made by an admin take effect without a manual reload.
    const onFocus = () => { if (localStorage.getItem('token')) refreshUser(); };
    window.addEventListener('focus', onFocus);

    // Listen for global auth failure events (dispatched by axios interceptor)
    const onUnauthorized = () => {
      // Clear storage and update state; this keeps logout logic centralized
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      initialLoadRef.current = false;
    };

    window.addEventListener('auth:unauthorized', onUnauthorized);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('auth:unauthorized', onUnauthorized);
    };
  }, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [activeToast, setActiveToast] = useState(null);
  const initialLoadRef = useRef(false);
  
  // Socket setup
  useEffect(() => {
    let newSocket;
    const token = localStorage.getItem('token');
    
    if (user && token) {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:9410/api';
      const defaultSocketUrl = apiUrl.replace('/api', '');
      newSocket = io(process.env.REACT_APP_SOCKET_URL || defaultSocketUrl, {
        auth: { token },
        path: '/socket.io'
      });
      setSocket(newSocket);

      // Listen for notifications globally
      const handleNewNotification = (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setActiveToast(notification);

        // Play notification sound
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
        const soundUrl = isMac ? '/sounds/that-was-quick-606.m4r' : '/sounds/that-was-quick-606.mp3';
        const audio = new Audio(soundUrl);
        audio.play().catch(err => console.log('Browser blocked audio playback:', err));

        // Show Native Browser Notification unconditionally
        if ("Notification" in window) {
          if (Notification.permission === "granted") {
            new Notification(notification.title || 'New Notification', {
              body: notification.message || ''
            });
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission().then(permission => {
              if (permission === "granted") {
                new Notification(notification.title || 'New Notification', {
                  body: notification.message || ''
                });
              }
            });
          }
        }
      };

      newSocket.on('new-notification', handleNewNotification);
    }

    return () => {
      if (newSocket) newSocket.close();
    };
  }, [user]);

  // Fetch initial notifications globally
  useEffect(() => {
    if (user) {
      api.get('/notifications')
        .then(res => {
          const fetchedNotifications = res.data || [];
          setNotifications(fetchedNotifications);
          
          if (!initialLoadRef.current) {
            initialLoadRef.current = true;
            const unread = fetchedNotifications.filter(n => !n.isRead);
            if (unread.length > 0) {
              if (unread.length === 1) {
                setActiveToast(unread[0]);
              } else {
                setActiveToast({
                  title: 'New Notifications',
                  message: `You have ${unread.length} unread notifications waiting for you.`,
                  link: '/notifications'
                });
              }
              
              const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0;
              const soundUrl = isMac ? '/sounds/that-was-quick-606.m4r' : '/sounds/that-was-quick-606.mp3';
              const audio = new Audio(soundUrl);
              audio.play().catch(err => console.log('Browser blocked audio on load:', err));
            }
          }
        })
        .catch(err => console.error('Error fetching notifications:', err));
    }
  }, [user]);

  useEffect(() => {
    setUnreadNotificationsCount(notifications.filter(n => !n.isRead).length);
  }, [notifications]);

  const login = async (email, password) => {
    try {
      console.log('AuthContext: Attempting login for', email);
      const response = await api.post('/auth/login', { email, password });
      console.log('AuthContext: Login response received', response.data);
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      console.log('AuthContext: User state updated', user);
      return { success: true };
    } catch (error) {
      console.error('AuthContext: Login error', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    initialLoadRef.current = false;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const hasPermission = (module, action) => {
    if (!user || !user.role) return false;
    
    if (!user.role.permissions) return false;
    
    const modulePermission = user.role.permissions.find(p => p.module && p.module.toLowerCase() === module.toLowerCase());
    return modulePermission && modulePermission.actions.includes(action);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, hasPermission, refreshUser, socket, unreadNotificationsCount, setUnreadNotificationsCount, notifications, setNotifications, activeToast, setActiveToast }}>
      {children}
    </AuthContext.Provider>
  );
};
